import { Button, Flex, Grid, Image, Select, Text, type FlexProps } from '@chakra-ui/react'
import type { Table } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import ArrowLeft from '../../images/arrowLeft.svg'
import ArrowRight from '../../images/arrowRight.svg'

export type TanstackPaginationProps<T> = {
  table: Table<T>
  maxButtonCount?: number
  pagesProps?: FlexProps
  countProps?: FlexProps
  textAfterNumberOfRecords?: string
  totalCount: number
}

const Page = ({
  pageNumber,
  isCurrent,
  onClick,
}: {
  pageNumber: number
  isCurrent: boolean
  onClick: (page: number) => void
}) => {
  return (
    <Button
      type="button"
      onClick={() => onClick(pageNumber)}
      variant={isCurrent ? 'solid' : 'ghost'}
      colorScheme={isCurrent ? 'primary' : 'white'}
    >
      {pageNumber}
    </Button>
  )
}

const range = (start: number, stop: number, step: number) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step)


export const TanstackPagination = <T,>({
  table,
  maxButtonCount = 5,
  pagesProps,
  countProps,
  textAfterNumberOfRecords = '',
  totalCount
}: TanstackPaginationProps<T>) => {
  const pageSizeOptions = [15, 50, 100, -1].filter(x => x <= totalCount)
  const displayedCount = table.getPrePaginationRowModel().rows.length
  const currentPage = table.getState().pagination.pageIndex + 1
  const pageSize = table.getState().pagination.pageSize
  const totalPagesCount = table.getPageCount()
  const [showAll, setShowAll] = useState(false)

  // pokud je zobrazeno všechno, nastavíme velikost stránky na celkový počet záznamů
  useEffect(() => {
    if (showAll && totalCount > 0) {
      table.setPageSize(totalCount)
    }
  }, [showAll, totalCount])

  if (displayedCount === 0) return null

  // stránkovací rozsah
  if (maxButtonCount > totalPagesCount) maxButtonCount = totalPagesCount

  const startPage = Math.floor(currentPage - (maxButtonCount / 2 - 1))
  let start = startPage < 1 ? 1 : startPage
  let end = start + maxButtonCount - 1

  if (end > totalPagesCount) {
    end = totalPagesCount
    start = totalPagesCount - maxButtonCount + 1
    start = start < 1 ? 1 : start
  }

  const pages = range(start, end, 1)

  const setCurrentPage = (page: number) => {
    table.setPageIndex(page - 1)
  }

  return (
    <Flex flexGrow={1} flexDir={'column'} justifyContent={'end'}>
      <Grid py={2} px={4} gridTemplateColumns={'auto 1fr auto'}>
        {pageSizeOptions.length > 1 &&
          <Flex align="center" gap={2} px={4} py={2}>
            <Text whiteSpace="nowrap">Záznamů na stránku:</Text>
            <Select
              width="auto"
              value={pageSize == totalCount ? -1 : pageSize}
              size='sm'
              onChange={(e) => {
                const selected = Number(e.target.value)
                table.setPageIndex(0) // reset na začátek

                const showAll = selected === -1
                table.setPageSize(showAll ? totalCount : selected) // -1 znamená všechny záznamy
                setShowAll(showAll)
              }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size === -1 ? 'Vše' : size}
                </option>
              ))}
            </Select>
          </Flex>
        }

        <Flex justifyContent="center" {...pagesProps}>
          {start > 1 && (
            <Button
              variant="ghost"
              colorScheme="white"
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              <Image src={ArrowLeft} />
            </Button>
          )}

          {pages.length > 1 &&
            pages.map(page => (
              <Page
                key={page}
                pageNumber={page}
                isCurrent={page === currentPage}
                onClick={setCurrentPage}
              />
            ))
          }

          {end < totalPagesCount && (
            <Button
              variant="ghost"
              colorScheme="white"
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              <Image src={ArrowRight} />
            </Button>
          )}
        </Flex>

        <Flex my={2} justifyContent="flex-end" {...countProps} fontSize="sm">
          celkem {totalCount}{' '}
          {totalCount === 1
            ? 'záznam'
            : totalCount < 5 && totalCount !== 0
              ? 'záznamy'
              : 'záznamů'}{' '}
          {textAfterNumberOfRecords}
        </Flex>
      </Grid>
    </Flex>
  )
}